import moment from "moment";

const canUseSymbol = typeof Symbol === 'function' && Symbol.for;
const REACT_ELEMENT_TYPE = canUseSymbol ? Symbol.for('react.element') : 0xeac7;
const OneDayMiliseconds = 86400000;

export const ONE_MB = 1048576;
export const NUMBER_MAX_INT32 = Math.pow(2, 15);
export const EmailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
export const UrlRegex = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/g;
export const UuidRegex = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
export const IpAddressRegex = /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])(\/(3[0-2]|[1-2]?\d))?$/;

/**
 * Realiza um DEBOUNCE de uma função, ou seja, garante que a função seja executada apenas após um determinado período de tempo desde a última vez que foi chamada, evitando execuções excessivas em eventos como digitação ou redimensionamento de janela.
 * @param fn
 * @param delay
 * @returns
 */
export function debounce<F extends (...params: any[]) => void>(fn: F, delay: number) {
    let timeoutID: number = null!;
    return function (this: any, ...args: any[]) {
        clearTimeout(timeoutID);
        timeoutID = window.setTimeout(() => fn.apply(this, args), delay);
    } as F;
}

/**
 * Realiza um CHECK de um valor, retornando um valor padrão caso o valor seja nulo ou indefinido.
 * @param someObject
 * @param defaultValue
 * @returns
 */
export function nprop<T>(someObject: T, defaultValue: T = {} as T): T {
    if (typeof someObject === 'undefined' || someObject === null)
        return defaultValue;
    else
        return someObject;
}

const ISO_8601 = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2,3})(.[0-9]+)?Z/;
const DATE_TEST = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2,3})/;
const JSON_TEST = /^\{.*\}$/;

/**
 * Funções Utilitárias.
 */
export default class LibUtils {

    /**
     * Verifica se uma string é uma URL válida.
     * @param url
     * @returns
     */
    static isValidURL(url: string | null): boolean {
        if (url == null || url.trim() == "") return false;

        try {
            return Boolean(new URL(url));
        } catch {
            return false;
        }
    }

    /**
     * Realiza o DECODE de um texto contendo entidades HTML, convertendo as entidades para seus caracteres correspondentes.
     * @param text
     * @returns
     */
    static decodeHtml(text: string) {
        var textArea = document.createElement('textarea');
        textArea.innerHTML = text;
        return textArea.value;
    }

    /**
     * Verifica se o ambiente de execução é o modo de desenvolvimento, considerando a variável de ambiente NODE_ENV.
     * @returns
     */
    static inDevMode() {
        return (!process.env.NODE_ENV || process.env.NODE_ENV === 'development');
    }

    /**
     * Obtém a localidade do navegador, considerando as configurações de idioma do usuário.
     * @returns
     */
    static getBrowserLocale() {
        return (new Intl.NumberFormat()).resolvedOptions().locale;
    }

    /**
     * Obtém os separadores de número decimal e de milhar de uma localidade, considerando as configurações de idioma do usuário.
     * @param locale
     * @returns
     */
    static getLocaleNumberSeparators = (locale: string) => {
        const thousandSeparator = Intl.NumberFormat(locale)
            .format(11111)
            .replace(/\p{Number}/gu, "");

        const decimalSeparator = Intl.NumberFormat(locale)
            .format(1.1)
            .replace(/\p{Number}/gu, "");

        return {
            decimalSeparator,
            thousandSeparator
        };
    };

    /**
     * Verifica se o valor não é nulo.
     * @param value
     * @returns
     */
    static isNonNullObject(value: any) {
        return !!value && typeof value === 'object';
    }

    /**
     * Verificar se a entrada é um número.
     * @param input
     * @returns
     */
    static matchIsNumeric(input: string | number) {
        const isNumber = typeof input === 'number';
        const isString = typeof input === 'string';
        return (isNumber || (isString && input !== '')) && !isNaN(Number(input));
    }

    /**
     *
     * @param d Verifica se a data é válida.
     * @returns
     */
    static isValidDate(d: any) {
        return d instanceof Date && isFinite(d.getTime());
    }

    /**
     * Calcula a diferença de dias entre duas data.
     * @param startDate
     * @param endDate
     * @returns
     */
    static diffDays(startDate: Date, endDate: Date) {
        return (endDate.setHours(0, 0, 0, 0) - startDate.setHours(0, 0, 0, 0)) / OneDayMiliseconds;
    }

    /**
     * Calcula a Próxima Hora Cheia
     * @returns
     */
    static getNextHour() {
        return moment().minutes(0).seconds(0).milliseconds(0).add(1, "hour").toDate();
    }

    static isExpired(compareDate: Date, dts: Date, dte: Date) {
        if (dts == null && dte == null) return false;
        if (dts != null && dte != null && !(compareDate >= dts && compareDate <= dte)) return false;
        if (dte != null && compareDate >= dte) return false;
        return true;
    }

    /**
     * Verifica se as datas são iguais considerando apenas o ano, mês e dia.
     * @param date1
     * @param date2
     * @returns
     */
    static isSameDay(date1: Date | null, date2: Date | null): boolean {
        if (date1 == null || date2 == null) return false;
        return moment(date1).isSame(date2, "day");
    }

    /**
     * Verifica se as datas são iguais considerando apenas o ano, mês, dia, hora e minuto.
     * @param d1
     * @param d2
     * @returns
     */
    static isEqualDateMinutes(d1: Date, d2: Date) {
        return d1.getFullYear() == d2.getFullYear()
            && d1.getMonth() == d2.getMonth()
            && d1.getDate() == d2.getDate()
            && d1.getHours() == d2.getHours()
            && d1.getMinutes() == d2.getMinutes();
    }

    /**
     * Obtém o separador de data de uma localidade.
     * @param locale
     * @returns
     */
    static getLocaleDateAtSeparator = (locale: string) => {
        const tokens = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'long' }).format(new Date()).split(' ');
        return tokens.length > 6 ? tokens[6] : "";
    };

    /**
     * Executa o PARSE de um valor BOOLEANO.
     * @param val
     * @returns
     */
    static booleanParse = function (val: any) {
        var falsy = /^(?:f(?:alse)?|no?|0+)$/i;
        return !falsy.test(val) && !!val;
    };

    /**
     * Executa o PARSE de um valor FLOAT respeitando as configurações de uma Localidade.
     * @param stringNumber
     * @param locale
     * @returns
     */
    static parseFloatLocale(stringNumber: string, locale: string) {
        const separators = LibUtils.getLocaleNumberSeparators(locale);

        return parseFloat(
            stringNumber
                .replace(new RegExp("\\" + separators.thousandSeparator, "g"), "")
                .replace(new RegExp("\\" + separators.decimalSeparator), ".")
        );
    }

    /**
     * Arredonda um número.
     * @param num
     * @param decimals
     * @returns
     */
    static round(num: number, decimals: number) {
        var n = Math.pow(10, decimals);
        return Math.round(parseInt((n * num).toFixed(decimals))) / n;
    }

    /**
     * Realiza o MASK de um e-mail, ocultando parte do nome de usuário para proteger a privacidade do usuário.
     * @param email
     * @returns
     */
    static maskEmail(email: string): string {
        if (!email || !email.includes('@')) {
            return '';
        }

        const [username, domain] = email.split('@');

        // 1. Mascarar o Usuário (Username)
        let maskedUsername = '';
        if (username.length <= 2) {
            maskedUsername = `${username[0]}*`;
        } else {
            const firstLetter = username[0];
            const lastLetter = username[username.length - 1];
            const asterisks = '*'.repeat(username.length - 2);
            maskedUsername = `${firstLetter}${asterisks}${lastLetter}`;
        }

        // 2. Mascarar o Domínio (Domain)
        // Encontra a posição do ÚLTIMO ponto (ex: hotmail.com.br -> pega o índice do ponto antes do 'br')
        const lastDotIndex = domain.lastIndexOf('.');

        let maskedDomain = domain;

        if (lastDotIndex > 0) {
            const providerAndMiddle = domain.substring(0, lastDotIndex); // Ex: "hotmail.com"
            const lastSuffix = domain.substring(lastDotIndex); // Ex: ".br" ou ".com"

            let maskedProvider = '';
            // Mantém a primeira letra do provedor e mascara o resto até chegar no ponto
            if (providerAndMiddle.length <= 1) {
                maskedProvider = '*';
            } else {
                const firstLetterProv = providerAndMiddle[0];
                const asterisksProv = '*'.repeat(providerAndMiddle.length - 1);
                maskedProvider = `${firstLetterProv}${asterisksProv}`;
            }

            // Junta o provedor mascarado com o sufixo que já contém o "."
            maskedDomain = `${maskedProvider}${lastSuffix}`;
        }

        // 3. Juntar as partes tratadas
        return `${maskedUsername}@${maskedDomain}`;
    }

    /**
     * Verifica se o valor é um RegExp, Date ou React Element.
     * @param value
     * @returns
     */
    static isSpecialValue(value: any) {
        var stringValue = Object.prototype.toString.call(value);
        return stringValue === '[object RegExp]'
            || stringValue === '[object Date]'
            || LibUtils.isReactElement(value);
    }

    /**
     * Verifica se o valor é um React Element.
     * @param value
     * @returns
     */
    static isReactElement(value: any): boolean {
        return value.$$typeof === REACT_ELEMENT_TYPE;
    }

    /**
     * Verifica se os objetos são iguais considerando apenas as propriedades de primeiro nível.
     * @param newObj
     * @param prevObj
     * @returns
     */
    static isEqual(newObj: any, prevObj: any): boolean {
        if (!newObj || !prevObj) {
            return newObj === prevObj;
        }

        var isDiff =
            Object
                .keys(Object.assign({}, newObj, prevObj))
                .find((key) => newObj[key] !== prevObj[key]);

        return isDiff == null;
    }

    /**
     * Verifica se todas as propriedades de um objeto possuem um valor específico, desconsiderando as propriedades de um array de exclusão.
     * @param source
     * @param compareValue
     * @param excludeFields
     * @returns
     */
    static isAllPropertiesWithValue(source: any, compareValue: any, excludeFields: string[] | null = null): boolean {
        if (source == null)
            return compareValue === null;

        for (const prop in source) {
            if (excludeFields != null && excludeFields.findIndex(x => x == prop) > -1) continue;
            if (source[prop] != compareValue) return false;
        }

        return true;
    }

    /**
     * Verifica se um arquivo é aceito considerando uma string ou array de tipos de arquivos aceitos.
     * @param file
     * @param acceptedFiles
     * @returns
     */
    static isFileAccept = function (file: File, acceptedFiles: string | string[]) {
        if (file && acceptedFiles) {
            const acceptedFilesArray = Array.isArray(acceptedFiles)
                ? acceptedFiles
                : acceptedFiles.split(',');
            const fileName = file.name || '';
            const mimeType = (file.type || '').toLowerCase();
            const baseMimeType = mimeType.replace(/\/.*$/, '');

            return acceptedFilesArray.some(type => {
                const validType = type.trim().toLowerCase();
                if (validType.charAt(0) === '.') {
                    return fileName.toLowerCase().endsWith(validType);
                } else if (validType.endsWith('/*')) {
                    // This is something like a image/* mime type
                    return baseMimeType === validType.replace(/\/.*$/, '');
                }
                return mimeType === validType;
            });
        }
        return true;
    };

    /**
     * Obtém a primeira palavra de um texto.
     * @param text
     * @returns
     */
    static firstWord(text: string): string {
        return text.replace(/ .*/, '');
    }

    /**
     * Obtém as iniciais de um nome.
     * @param name
     * @returns
     */
    static avatarName(name: string) {
        const s = name.split(' ');
        return `${s[0][0]}${(s.length > 1 ? s[1][0] : "")}`;
    }

    /**
     * Realiza um SUBSTRING seguro, evitando erros caso o valor seja nulo ou o tamanho seja maior que o valor.
     * @param val
     * @param from
     * @param lenght
     * @param suffix
     * @returns
     */
    static safeSubstr(val: string | undefined | null, from: number, lenght?: number, suffix?: string) {
        return val == undefined
            ? val
            : val.substring(from, lenght && lenght < val.length ? lenght : undefined) + (lenght && lenght >= val.length ? (suffix ?? "") : "");
    }

    /**
     * Realiza um SPLIT por quebra de linha, considerando os diferentes tipos de quebra de linha existentes.
     * @param text
     * @returns
     */
    static splitByNewLine(text: string) {
        return text.split(/\r|\n\r|\n/g);
    }

    /**
     * Remove acentos de um texto.
     * @param text
     * @returns
     */
    static removeAccents(text: string) {
        return text.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
    }

    /**
     * Realiza um TOKENIZER em um texto, considerando palavras, hashtags, menções, pontuações e quebras de linha como tokens separados.
     * @param text
     * @returns
     */
    static tokenizerString(text: string): string[] {
        const tokens = [];

        const matches = text.match(/#([0-9a-zA-Z\u00C0-\u017F\-,]+)([\s|\.|\!|\?|/]|\\n|$)|\[([0-9a-zA-Z\u00C0-\u017F\-,]+)([\]|\s]|\\n|$)|([0-9a-zA-Z\u00C0-\u017F\-,])+([\s|\.|\!|\?|/]|\\n|$)|(\\n)|(\s|\.|\!|\?$)/gm);
        if (matches != null) {
            // Restaura espaços.
            var newline = false;
            for (var i = 0; i < matches.length; i++) {
                var word = matches[i].trim();

                if (word.endsWith("\\n")) {
                    word = word.substring(0, word.length - 2);
                    newline = true;
                }
                else if (word.endsWith("\n")) {
                    word = word.substring(0, word.length - 1);
                    newline = true;
                }

                tokens.push(word);

                if (newline) {
                    tokens.push('\\n');
                    newline = false;
                }
                else if (word != "." && i < matches.length - 1)
                    tokens.push(' ');
            }
        }

        return tokens;
    }

    /**
     * Realiza a cópia de propriedades de um objeto para outro, considerando um callback de análise para customizar o processo de cópia.
     * @param source
     * @param destination
     * @param analizeCallback
     */
    static copyProperties<S, D>(source: S, destination: D, analizeCallback?: (prop: keyof S, value: any) => { prop: keyof D, value: any; } | undefined): void {
        for (const prop in source) {
            if (analizeCallback) {
                const v = analizeCallback(prop, source[prop]);
                if (v != undefined) destination[v.prop] = v.value;
            }
            else {
                (destination as any)[prop] = source[prop];
            }
        }
    }

    /**
     * Cria um novo objeto de um tipo específico a partir de um objeto fonte, considerando um callback de análise para customizar o processo de cópia.
     * @param source
     * @param analizeCallback
     * @returns
     */
    static createFrom<S, D>(source: S, analizeCallback?: (prop: keyof S, value: any) => { prop: keyof D, value: any; } | undefined): D {
        let destination = {} as D;
        this.copyProperties(source, destination, analizeCallback);
        return destination;
    }

    /**
     * Realiza uma cópia profunda de um objeto, considerando os tipos especiais de RegExp, Date e React Element.
     * @param obj
     * @returns
     */
    static deepCopy(obj: any): any {
        return structuredClone(obj);
    }

    /**
     * Compara dois objetos considerando as propriedades de primeiro nível, desconsiderando as propriedades de um array de exclusão e considerando os tipos especiais de RegExp, Date e React Element.
     * @param x
     * @param y
     * @param excludedProperty
     * @returns
     */
    static compareObjects(x: Object, y: Object, excludedProperty: string[] = []): boolean {
        if (x === y) return true;
        // if both x and y are null or undefined and exactly the same

        if (!(x instanceof Object) || !(y instanceof Object)) return false;
        // if they are not strictly equal, they both need to be Objects

        if (x.constructor !== y.constructor) return false;
        // they must have the exact same prototype chain, the closest we can do is
        // test there constructor.

        const a: { [index: string]: any; } = x;
        const b: { [index: string]: any; } = y;

        for (var p in x) {
            if (excludedProperty.includes(a[p]) || excludedProperty.includes(b[p])) continue;
            if (!x.hasOwnProperty(p)) continue;

            // other properties were tested using x.constructor === y.constructor

            if (!y.hasOwnProperty(p)) return false;
            // allows to compare x[ p ] and y[ p ] when set to undefined

            if (a[p] === b[p]) continue;
            // if they have the same strict value or identity then they are equal

            if (typeof (a[p]) !== "object") return false;
            // Numbers, Strings, Functions, Booleans must be strictly equal

            if (!LibUtils.compareObjects(a[p], b[p], excludedProperty)) return false;
            // Objects and Arrays must be tested recursively
        }

        for (p in y) {
            if (y.hasOwnProperty(p) && !x.hasOwnProperty(p)) return false;
            // allows x[ p ] to be set to undefined
        }
        return true;
    }

    /**
     * Compara dois objetos profundamente considerando os tipos especiais de RegExp, Date e React Element, e considerando um callback de análise para customizar o processo de comparação.
     * @param obj1
     * @param obj2
     * @param replacer
     * @returns
     */
    static deepCompare(obj1: any, obj2: any, replacer?: (this: any, key: string, value: any) => any) {
        return JSON.stringify(obj1, replacer) === JSON.stringify(obj2, replacer);
    }

    /**
     * Converte um Enum para um Array de Chave e Valor.
     * @param t
     * @returns
     */
    static convertEnumToArray<T extends EnumType>(t: T): Array<{ key: number, value: string; }> {
        return Object.keys(t)
            .map(Number)
            .filter(x => !isNaN(x))  //.filter(Boolean) deconsidera o primeiro item do enum se o valor for 0
            .reduce((p, c) => [...p, { key: c, value: t[c] }], [] as Array<{ key: number, value: string; }>);
    }

    /**
     * Converte os valores nulos de um objeto para undefined, considerando os tipos especiais de RegExp, Date e React Element.
     * @param obj
     * @returns
     */
    static convertNullToUndefined(obj: any): any {
        if (obj === null || obj === undefined) {
            return undefined;

        }
        if (typeof obj !== 'object') {
            return obj;

        }
        if (obj instanceof Array) {
            return obj;
        }

        return Object.keys(obj).reduce((result, key) => ({
            ...result,
            [key]: this.convertNullToUndefined(obj[key])
        }), {});
    }

    /**
     * Converte os valores nulos ou vazios de um objeto para undefined, considerando os tipos especiais de RegExp, Date e React Element.
     * @param obj
     * @returns
     */
    static convertNullOrEmptyToUndefined(obj: any): any {
        if (obj === null || obj === undefined || obj === "") {
            return undefined;
        }

        if (typeof obj !== 'object') {
            return obj;
        }

        if (obj instanceof Array) {
            return obj;
            //return obj.map(this.convertNullOrEmptyToUndefined);
        }

        return Object.keys(obj).reduce((result, key) => {
            return {
                ...result,
                [key]: this.convertNullOrEmptyToUndefined(obj[key])
            };
        }, {});
    }

    /**
     * Converte um Enum para um Array de Chave e Valor filtrando apenas os itens que possuem os bits definidos em um valor de comparação, considerando o Enum como um conjunto de flags.
     * @param t
     * @param bit
     * @returns
     */
    static bitwiseEnumToArray<T extends EnumType>(t: T, bit: number): Array<{ key: number, value: string; }> {
        return LibUtils.convertEnumToArray<T>(t).filter(p => (p.key & bit) === p.key);
    }

    /**
     * Obtém a soma de todas as chaves de um Enum, considerando o Enum como um conjunto de flags.
     * @param t
     * @returns
     */
    static bitwiseEnumAllFlagsKey<T extends EnumType>(t: T) {
        return Object.keys(t).map(Number).filter(Boolean).reduce((p, c) => p + (c > 0 ? c : 0), 0);
    }

    /**
     * Obtém a extensão de um arquivo a partir do nome do arquivo.
     * @param filename
     * @returns
     */
    static getFileExtension(filename: string): string | undefined {
        return filename.split('.').pop();
    }

    /**
     * Obtém o nome de um arquivo sem a extensão a partir do nome do arquivo.
     * @param filename
     * @returns
     */
    static getFilenameWithoutExtension(filename: string): string {
        var p = filename.lastIndexOf('.');
        return p == -1 ? filename : filename.substring(0, p);
    }

    /**
     * Substitui um item em um array por outro valor, retornando um novo array, sem modificar o array original.
     * @param array
     * @param index
     * @param value
     * @returns
     */
    static arrayReplaceAt = function <T extends unknown>(array: Array<T>, index: number, value: T) {
        const ret = array.slice(0);
        ret[index] = value;
        return ret;
    };

    /**
     * Remove uma propriedade de um objeto, retornando um novo objeto sem modificar o objeto original, considerando os tipos especiais de RegExp, Date e React Element.
     * @param obj
     * @param propertyName
     * @returns
     */
    static deleteObjectProp(obj: any, propertyName: string) {
        if (obj && obj[propertyName]) {
            let newObj = { ...obj };
            delete newObj[propertyName];
            return newObj;
        }
        return obj;
    }

    /**
    * Restaura um objeto obtido pela API.
    * @param v
    */
    static restoreResponse(v: any) {
        Object.keys(v).forEach((k) => {
            try {
                if (v.hasOwnProperty(k)) {
                    if (typeof v[k] == "object" && v[k] !== null) {
                        this.restoreResponse(v[k]);
                    }
                    else {
                        if (ISO_8601.test(v[k]) || DATE_TEST.test(v[k])) {
                            v[k] = new Date(Date.parse(v[k]));
                        }
                        else if (JSON_TEST.test(v[k])) {
                            v[k] = JSON.parse(v[k]);
                        }
                    }
                }
            }
            catch {
                //v[k] = v[k];
            }
        });
    }
}
